import { useEffect, useCallback } from 'react';
import * as fetcher from '@hydra/utils/fetcher';
import { useI18n } from '@hydra/common/customHooks';

import { SUFIX_STATE_GA_4 } from '@hydra/utils/trafficGa4Util';

const settings =
  `directories=no,
  titlebar=no,
  toolbar=no,
  location=no,
  status=no,
  menubar=no,
  scrollbars=no,
  resizable=no,
  height=600,
  width=900`;

async function syncGoogleAnalytics (projectUrl: string, isGA4?: boolean) {
  const projUrl = isGA4 ? `${projectUrl}${SUFIX_STATE_GA_4}` : projectUrl;
  const state = btoa(encodeURIComponent(projUrl));
  const windowRef = window.open('', '_blank', settings);
  const url = isGA4
    ? await fetcher.get('/api/third-party/auth', { searchParams: { state, isFour: '1' } }).text()
    : await fetcher.get('/api/third-party/auth', { searchParams: { state } }).text();
  windowRef && windowRef.location.assign(url);
}

async function syncGoogleSeacrhConsole (projectUrl: string, locale: string) {
  const state = btoa(encodeURIComponent(projectUrl));
  const origin = window.location.origin;
  const redirectUrl = `${origin}/${locale}/gwt/oauth2callback`;
  const windowRef = window.open('', '_blank', settings);
  const url = await fetcher.get('/api/gsc/auth', {
    searchParams: { state, redirectUrl }
  }).text();
  windowRef && windowRef.location.assign(url);
}

async function syncFacebook (projectUrl: string, locale: string) {
  const origin = window.location.origin;
  const redirectUrl = encodeURIComponent(`${origin}/${locale}/fb/oauth2callback`);
  const windowRef = window.open('', '_blank', settings);
  const json = await fetcher.get('/api/fb/auth', {
    searchParams: { redirectUrl }
  }).json();
  const url = new URL(json.data.authUrl);
  url.searchParams.append('state', projectUrl);
  windowRef && windowRef.location.assign(url.href);
}

function useSyncThirdParty ({
  thirdPartyAccount,
  projectUrl,
  updateData
}: {
  thirdPartyAccount: string
  projectUrl: string
  updateData: React.Dispatch<any>
}) {
  const { locale } = useI18n();
  const handleSyncAccount = async () => {
    if (thirdPartyAccount === 'GAData') {
      return syncGoogleAnalytics(projectUrl);
    } else if (thirdPartyAccount === 'GA4Data') {
      const isGA4 = true;
      return syncGoogleAnalytics(projectUrl, isGA4);
    } else if (thirdPartyAccount === 'GSCData') {
      return syncGoogleSeacrhConsole(projectUrl, locale);
    } else if (thirdPartyAccount === 'fbPageId') {
      return syncFacebook(projectUrl, locale);
    }
  };

  const handleOnMessage = useCallback((e) => {
    if (
      e.origin !== window.location.origin ||
      typeof e.data !== 'string' ||
      (typeof e.data === 'string' && !e.data.includes(thirdPartyAccount))) {
      return;
    }
    const parsed = JSON.parse(e.data);
    updateData(parsed[thirdPartyAccount]);
  }, [updateData, thirdPartyAccount]);

  useEffect(() => {
    window.addEventListener('message', handleOnMessage);
    return () => {
      window.removeEventListener('message', handleOnMessage);
    };
  }, [handleOnMessage]);

  return { handleSyncAccount };
}

export default useSyncThirdParty;
